import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import {
  Button,
  Input,
  Label,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui';
import { User, Lock, Shield, Eye, EyeOff, Check } from 'lucide-react';
import GdprSettingsCard from '../../components/gdpr/GdprSettingsCard';

/**
 * Pagina impostazioni profilo
 */
function SettingsPage() {
  const { user, updateProfile, changePassword, enable2FA, disable2FA } = useAuth();
  const { toast } = useToast();

  // Stato form profilo
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
  });
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Stato form password
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  // Stato 2FA
  const [isToggling2FA, setIsToggling2FA] = useState(false);
  const [showDisable2FADialog, setShowDisable2FADialog] = useState(false);
  const [disable2FAPassword, setDisable2FAPassword] = useState('');

  const handleProfileChange = (field, value) => {
    setProfileData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setIsSavingProfile(true);

    try {
      await updateProfile(profileData);
    } catch (error) {
      // Errore già gestito nel context
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Errore', 'Le password non coincidono');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.error('Errore', 'La nuova password deve avere almeno 8 caratteri');
      return;
    }

    setIsSavingPassword(true);

    try {
      await changePassword(
        passwordData.currentPassword,
        passwordData.newPassword,
        passwordData.confirmPassword
      );
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      // Errore già gestito nel context
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleToggle2FA = async () => {
    if (user.twoFactorEnabled) {
      // Apri dialog per chiedere password
      setShowDisable2FADialog(true);
      return;
    }

    setIsToggling2FA(true);
    try {
      await enable2FA();
    } catch (error) {
      // Errore già gestito nel context
    } finally {
      setIsToggling2FA(false);
    }
  };

  const handleConfirmDisable2FA = async () => {
    setIsToggling2FA(true);
    try {
      await disable2FA(disable2FAPassword);
      setShowDisable2FADialog(false);
      setDisable2FAPassword('');
    } catch (error) {
      // Errore già gestito nel context
    } finally {
      setIsToggling2FA(false);
    }
  };

  const roleLabels = {
    admin: 'Amministratore',
    operator: 'Operatore',
    client: 'Cliente',
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-foreground">Impostazioni</h1>
        <p className="text-sm sm:text-base text-muted-foreground">Gestisci il tuo profilo e la sicurezza</p>
      </div>

      {/* Info account */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-bold text-lg sm:text-xl">
                {user?.firstName?.[0]}
                {user?.lastName?.[0]}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="text-base sm:text-lg truncate">
                {user?.firstName} {user?.lastName}
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm truncate">{user?.email}</CardDescription>
              <Badge variant="secondary" className="mt-1 text-xs">
                {roleLabels[user?.role]}
              </Badge>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Form profilo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Informazioni personali
          </CardTitle>
          <CardDescription>Aggiorna i tuoi dati personali</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Nome</Label>
                <Input
                  id="firstName"
                  value={profileData.firstName}
                  onChange={(e) => handleProfileChange('firstName', e.target.value)}
                  placeholder="Mario"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Cognome</Label>
                <Input
                  id="lastName"
                  value={profileData.lastName}
                  onChange={(e) => handleProfileChange('lastName', e.target.value)}
                  placeholder="Rossi"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Telefono</Label>
              <Input
                id="phone"
                type="tel"
                value={profileData.phone}
                onChange={(e) => handleProfileChange('phone', e.target.value)}
                placeholder="+39 333 1234567"
              />
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={user?.email} disabled className="bg-muted" />
              <p className="text-xs text-muted-foreground">
                L'email non può essere modificata. Contatta l'assistenza se necessario.
              </p>
            </div>

            <Button type="submit" isLoading={isSavingProfile}>
              Salva modifiche
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Cambio password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Cambia password
          </CardTitle>
          <CardDescription>Aggiorna la tua password di accesso</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Password attuale</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showPasswords ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                  placeholder="••••••••"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">Nuova password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPasswords ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  placeholder="••••••••"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPasswords ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Minimo 8 caratteri, con maiuscola, minuscola e numero
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Conferma nuova password</Label>
              <Input
                id="confirmPassword"
                type={showPasswords ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                placeholder="••••••••"
              />
            </div>

            <Button
              type="submit"
              isLoading={isSavingPassword}
              disabled={!passwordData.currentPassword || !passwordData.newPassword}
            >
              Cambia password
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* 2FA */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Shield className="h-5 w-5 flex-shrink-0" />
            <span className="truncate">Autenticazione a due fattori (2FA)</span>
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Aggiungi un ulteriore livello di sicurezza al tuo account
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-sm sm:text-base">2FA via email</p>
                {user?.twoFactorEnabled ? (
                  <Badge variant="success">
                    <Check className="mr-1 h-3 w-3" />
                    Attivo
                  </Badge>
                ) : (
                  <Badge variant="secondary">Non attivo</Badge>
                )}
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {user?.twoFactorEnabled
                  ? 'Riceverai un codice di verifica via email ad ogni accesso'
                  : 'Attiva per ricevere un codice di verifica via email ad ogni accesso'}
              </p>
            </div>
            <Button
              variant={user?.twoFactorEnabled ? 'outline' : 'default'}
              onClick={handleToggle2FA}
              isLoading={isToggling2FA}
              className="w-full sm:w-auto min-h-[44px] touch-manipulation"
            >
              {user?.twoFactorEnabled ? 'Disattiva' : 'Attiva'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Privacy e Dati Personali (GDPR) */}
      <GdprSettingsCard />

      {/* Info cliente (solo per clienti) */}
      {user?.role === 'client' && (user?.guestName || user?.guestRoom) && (
        <Card>
          <CardHeader>
            <CardTitle>Informazioni ospite</CardTitle>
            <CardDescription>Dati relativi all'ospite in struttura</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {user.guestName && (
                <div>
                  <p className="text-sm text-muted-foreground">Nome ospite</p>
                  <p className="font-medium">{user.guestName}</p>
                </div>
              )}
              {user.guestRoom && (
                <div>
                  <p className="text-sm text-muted-foreground">Stanza</p>
                  <p className="font-medium">{user.guestRoom}</p>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Per modificare queste informazioni, contatta l'amministratore.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Dialog conferma disattivazione 2FA */}
      <Dialog open={showDisable2FADialog} onOpenChange={setShowDisable2FADialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disattiva autenticazione a due fattori</DialogTitle>
            <DialogDescription>
              Per disattivare la 2FA, inserisci la tua password per confermare.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="disable2fa-password">Password</Label>
              <Input
                id="disable2fa-password"
                type="password"
                value={disable2FAPassword}
                onChange={(e) => setDisable2FAPassword(e.target.value)}
                placeholder="Inserisci la tua password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDisable2FADialog(false);
                setDisable2FAPassword('');
              }}
            >
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDisable2FA}
              isLoading={isToggling2FA}
              disabled={!disable2FAPassword}
            >
              Disattiva 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SettingsPage;
